/*
|-------------------------------------------------------------------------------
| routes.js
|-------------------------------------------------------------------------------
| Contains all of the routes for the application
*/

/*
 * Imports Vue and VueRouter to extend with the routes.
 */
import Vue from 'vue';
import VueGtm from 'vue-gtm';
import VueRouter from 'vue-router';
import VueAnalytics from 'vue-analytics';
import store from './store';

/*
 * Makes a new VueRouter that we will use to run all of the routes
 * for the app.
*/
import AccountPage from './pages/account/AccountPage.vue';
import BlogPage from './pages/blog/BlogPage.vue';
import BlogCategoryPage from './pages/blog/BlogCategoryPage.vue';
import BlogSinglePage from './pages/blog/BlogSinglePage.vue';
import CartPage from './pages/shop/cart/CartPage.vue';
import CheckoutPage from './pages/shop/checkout/CheckoutPage.vue';
import HelpPage from './pages/help/HelpPage.vue';
import Homepage from './pages/Homepage.vue';
import LoginRegister from './pages/auth/LoginRegister.vue';
import Page from './pages/page/Page.vue';
import PaymentPage from './pages/shop/checkout/PaymentPage.vue';

import AccountRoutes from './routes/account';
import HelpRoutes from './routes/help';
// import ShopRoutes from './routes/shop';
import ProductCategoryPage from './pages/shop/product/ProductCategoryPage.vue';
import ProductSinglePage from './pages/shop/product/ProductSinglePage.vue';
import ThankYouPage from './pages/shop/checkout/ThankYouPage.vue';
import ResetPassword from './pages/auth/ResetPassword.vue';
import ResetPasswordConfirmation from './pages/auth/ResetPasswordConfirmation.vue';
import ProductSearchResultsPage from './pages/shop/product/ProductSearchResultsPage.vue';
import ContactPage from './pages/contact/ContactPage.vue';
import ComparePage from "./pages/compare/ComparePage.vue";
import ProductCategoriesPage from "./pages/shop/product/ProductCategoriesPage";

/*
 * Extends Vue to use Vue Router
 */
Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  scrollBehavior(to) {
    if (window.location.hash) {
      if (window.location.hash.length > 1) {
        return setTimeout(function() {
          $('html,body').stop().animate({scrollTop: $(window.location.hash).offset().top }, 500)
        }, 300);
      }
    }
    const ignoreScrollToTop = to.matched.some((record) => record.meta.ignoreScrollToTop);
    if (ignoreScrollToTop) return false;
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: Homepage,
      meta: {
        public: true,
      },
    },
    {
      path: '/login',
      name: 'login',
      component: LoginRegister,
      meta: {
        public: true,
      },
    },
    {
      path: '/register',
      name: 'register',
      component: LoginRegister,
      meta: {
        public: true,
      },
    },
    {
      path: '/password/reset',
      name: 'reset-password',
      component: ResetPassword,
      meta: {
        public: true,
      },
    },
    {
      path: '/password/reset/:token?',
      name: 'reset-password-confirmation',
      component: ResetPasswordConfirmation,
      meta: {
        public: true,
      },
    },
    {
      path: '/account',
      component: AccountPage,
      meta: {
        public: false,
      },
      children: [
        ...AccountRoutes,
      ],
    },
    {
      path: '/shop/product/:slug',
      name: 'shop.product',
      component: ProductSinglePage,
      meta: {
        public: true,
      },
    },
    {
      path: '/search/:query',
      name: 'search.results',
      component: ProductSearchResultsPage,
      meta: {
        public: true,
      },
    },
    {
      path: '/shop',
      name: 'shop.categories',
      component: ProductCategoriesPage,
      meta: {
        public: true,
      },
    },
    {
      path: '/shop/:slug/:page(\\d+)?',
      name: 'shop.category',
      component: ProductCategoryPage,
      meta: {
        public: true,
      },
    },
    {
      path: '/shop/:slug/brand/:brand/:page(\\d+)?',
      name: 'shop.category.brand',
      component: ProductCategoryPage,
      meta: {
        public: true,
        ignoreScrollToTop: true,
      },
    },
    {
      path: '/shop/:slug/:subCategory/:brand/:page(\\d+)?',
      name: 'shop.category.subcategory.brand',
      component: ProductCategoryPage,
      meta: {
        public: true,
        ignoreScrollToTop: true,
      },
    },
    {
      path: '/shop/:slug/:subCategory/:page(\\d+)?',
      name: 'shop.category.subcategory',
      component: ProductCategoryPage,
      meta: {
        public: true,
        ignoreScrollToTop: true,
      },
    },
    {
      path: '/cart',
      name: 'cart',
      component: CartPage,
      meta: {
        public: true,
      },
    },
    {
      path: '/payment',
      name: 'payment',
      component: PaymentPage,
      meta: {
        public: false,
      },
    },
    {
      path: '/thank-you',
      name: 'thank-you',
      component: ThankYouPage,
      meta: {
        public: false,
      },
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: CheckoutPage,
      meta: {
        public: false,
      },
    },
    {
      path: '/blog',
      name: 'blog.index',
      component: BlogPage,
      meta: {
        public: true,
      },
    },
    {
      path: '/blog/:slug',
      name: 'blog.single',
      component: BlogSinglePage,
      meta: {
        public: true,
        breadcrumb: [
          { name: 'Blog', route: 'blog.index' },
        ],
      },
    },
    {
      path: '/blog/category/:slug',
      name: 'blog.category.index',
      component: BlogCategoryPage,
      meta: {
        public: true,
        breadcrumb: [
          { name: 'Blog', route: 'blog.index' },
        ],
      },
    },
    {
      path: '/help',
      component: HelpPage,
      meta: {
        public: true,
      },
      children: [
        ...HelpRoutes,
      ],
    },
    {
      path: '/page/:slug',
      name: 'page',
      component: Page,
      meta: {
        public: true,
      },
    },
    {
      path: '/contact',
      name: 'contact',
      component: ContactPage,
      meta: {
        public: true,
      },
    },
    {
      path: '/contact#distributors',
      name: 'distributors',
      component: ContactPage,
      meta: {
        public: true,
      },
    },
    {
      path: '/contact#distributors',
      name: 'jobs',
      component: ContactPage,
      meta: {
        public: true,
      },
    },
    {
      path: '/compare',
      name: 'compare',
      component: ComparePage,
      meta: {
        public: true,
      },
    },
    // { path: '*', component: PageNotFound }
  ],
});

// eslint-disable-next-line consistent-return
router.beforeEach((to, from, next) => {
  const isPublic = to.matched.some((record) => record.meta.public);
  const isLoggedIn = store.getters['auth/isLoggedIn'];

  if (!isPublic && !isLoggedIn) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath }, // Store the full path to redirect the user to after login
    });
  }

  next();
});
Vue.use(VueAnalytics, {
  id: 'UA-106051351-3',
  ecommerce: {
    enabled: true,
    enhanced: true,
  },
  debug: {
    sendHitTask: process.env.NODE_ENV === 'production',
  },
  router,
});

Vue.use(VueGtm, {
  id: 'GTM-5K369SC',
  enabled: process.env.NODE_ENV === 'production', // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
  vueRouter: router, // Pass the router instance to automatically sync with router (optional)
});

export default router;
